import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';

type CamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<CamelCase<U>>}`
  : S;

type SnakeCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}_${SnakeCase<U>}`
  : S;

export type SnakeToCamel<T> = T extends unknown[]
  ? { [K in keyof T]: SnakeToCamel<T[K]> }
  : T extends object
    ? { [K in keyof T as CamelCase<string & K>]: SnakeToCamel<T[K]> }
    : T;

export type CamelToSnake<T> = T extends unknown[]
  ? { [K in keyof T]: CamelToSnake<T[K]> }
  : T extends object
    ? { [K in keyof T as SnakeCase<string & K>]: CamelToSnake<T[K]> }
    : T;

/**
 * Transform a given object from snake_case properties to camelCase properties
 * @param obj
 *
 * @example
 * transformKeysToCamelCase({ first_name: 'John', last_name: 'Doe' });
 * // Output: { firstName: 'John', lastName: 'Doe' }
 */
export const transformKeysToCamelCase = <
  Target extends object,
  Source extends object = Target,
>(
  obj: Source,
): Target => {
  if (Array.isArray(obj)) {
    return obj.map((v) => transformKeysToCamelCase(v)) as any;
  }

  const isValidObject =
    obj && typeof obj === 'object' && !(obj instanceof Date);

  if (isValidObject) {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = camelCase(key);

      result[camelKey] = transformKeysToCamelCase((obj as any)[key]);

      return result;
    }, {} as any);
  }

  return obj as unknown as Target;
};

/**
 * Transform a given object from camelCase properties to snake_case properties
 * @param obj
 *
 * @example
 * transformKeysToSnakeCase({ firstName: 'John', lastName: 'Doe' });
 * // Output: { first_name: 'John', last_name: 'Doe' }
 */
export const transformKeysToSnakeCase = <
  Target extends object,
  Source extends object = Target,
>(
  obj: Source,
): Target => {
  if (Array.isArray(obj)) {
    return obj.map((v) => transformKeysToSnakeCase(v)) as any;
  }

  const isValidObject =
    obj && typeof obj === 'object' && !(obj instanceof Date);

  if (isValidObject) {
    return Object.keys(obj).reduce((result, key) => {
      const snakeKey = snakeCase(key);

      result[snakeKey] = transformKeysToSnakeCase((obj as any)[key]);

      return result;
    }, {} as any);
  }

  return obj as unknown as Target;
};
